import type {
    AdditionalW9Dto,
    AdditionalW9Item,
    GovEnrollmentByState,
    IrsLetterDto,
    MedicareManagementDto,
    ProviderCredentialingPayoutAccountDto,
    ServiceAddressItem,
} from './types';
import { AETNA_FOCUS_MAX } from './constants';

// 四个步骤的完整性校验，步骤组件（Continue 置灰）与 review 行内编辑（Save 前拦截）共用同一实现

// 证书号选填，但填了要校验格式：5-10 位字母数字，无空格符号
export const CERT_RE = /^[A-Za-z0-9]{5,10}$/;

const govEntry = (list: GovEnrollmentByState[], state: string) => list.find((g) => g.state === state);

// 只在答 Yes 时校验号码：先填了号码再改成 No，输入框和红字都隐藏了，旧号码不能继续卡住 Continue
export const govCertInvalid = (list: GovEnrollmentByState[], state: string): boolean => {
    const entry = govEntry(list, state);
    const v = entry?.certificationNumber?.trim();
    return entry?.enrolled === true && !!v && !CERT_RE.test(v);
};

// 任一州 Medicare 答了 No（Klarity 代为申请 855I）→ 需要回答 managing employee
export const medicareNotEnrolledAnywhere = (states: string[], medicare: GovEnrollmentByState[]): boolean =>
    states.some((st) => govEntry(medicare, st)?.enrolled === false);

// 回填的 SSN 是掩码（•••-••-6789），带着 id 原样回传即保留原值；新输入的要 9 位数字
export const managerSsnValid = (ssn?: string, id?: string): boolean => {
    const v = (ssn || '').trim();
    if (!v) return false;
    if (/^\d{9}$/.test(v.replace(/[-\s]/g, ''))) return true;
    return !!id && v.includes('•');
};

// 原型：Yes/No 不预选必须作答；Yes 时每个人的姓、名、生日、SSN 都要齐
export const medicareManagementComplete = (mgmt?: MedicareManagementDto | null): boolean => {
    if (mgmt?.hasManager === undefined || mgmt?.hasManager === null) return false;
    if (!mgmt.hasManager) return true;
    const list = mgmt.managers || [];
    return (
        list.length > 0 &&
        list.every(
            (m) =>
                !!(m.firstName || '').trim() &&
                !!(m.lastName || '').trim() &&
                !!m.dateOfBirth &&
                managerSsnValid(m.ssn, m.id),
        )
    );
};

// 每州两个问题都要作答（Yes/No 均可）；已填的证书号格式需合法；任一州 Medicare 答 No 时
// managing employee 也要答完（不传 management 的调用方按旧口径，不校验这一项）
export const govComplete = (
    states: string[],
    medicare: GovEnrollmentByState[],
    medicaid: GovEnrollmentByState[],
    management?: MedicareManagementDto | null | false,
): boolean =>
    states.every(
        (st) =>
            govEntry(medicare, st)?.enrolled !== undefined &&
            govEntry(medicaid, st)?.enrolled !== undefined &&
            !govCertInvalid(medicare, st) &&
            !govCertInvalid(medicaid, st),
    ) &&
    (management === false ||
        !medicareNotEnrolledAnywhere(states, medicare) ||
        medicareManagementComplete(management || null));

// IRS letter：文件和信上的法定名称都要有才能 Continue
export const irsLetterComplete = (dto?: IrsLetterDto | null): boolean =>
    !!dto?.fileUrl && !!(dto.legalName || '').trim();

const ZIP_RE = /^\d{5}$/;
const filled = (v?: string | null) => !!(v || '').trim();

// 原型 payoutComplete：银行地址、持有人地址（勾了 Same as practice 除外）、routing 9 位、账号 ≥4 位。
// 银行证明文件不算在内（原型允许先跳过）
export const payoutComplete = (dto?: ProviderCredentialingPayoutAccountDto | null): boolean => {
    if (!dto) return false;
    const holderAddr =
        dto.holderSameAsPractice === true ||
        (filled(dto.holderStreet) &&
            filled(dto.holderCity) &&
            filled(dto.holderState) &&
            ZIP_RE.test(dto.holderZip || ''));
    return (
        filled(dto.bankName) &&
        filled(dto.accountHolder) &&
        /^\d{9}$/.test(dto.routingNumber || '') &&
        /^\d{4,17}$/.test(dto.accountNumber || '') &&
        filled(dto.bankStreet) &&
        filled(dto.bankCity) &&
        filled(dto.bankState) &&
        ZIP_RE.test(dto.bankZip || '') &&
        holderAddr
    );
};

// 原型 serviceAddressStatus 的校验规则与文案
export const serviceAddressError = (item: ServiceAddressItem | undefined): string => {
    if (!item) return '';
    const street = (item.addressLine1 || '').trim();
    const city = (item.city || '').trim();
    const zip = (item.zip || '').trim();
    const start = (item.practiceStartDate || '').trim();
    if (!street || !city || !zip || !start) return '';
    if (/\bp\.?\s*o\.?\s*box\b/i.test(street)) return 'A PO box is not accepted. Payers need a street address.';
    if (!/^\d/.test(street)) return 'The street address has to start with a building number.';
    if (!/^\d{5}$/.test(zip)) return 'Enter a 5-digit ZIP code.';
    return '';
};

export const serviceAddressItemReady = (item: ServiceAddressItem | undefined): boolean => {
    if (!item) return false;
    return (
        !!(item.addressLine1 || '').trim() &&
        !!(item.city || '').trim() &&
        !!(item.zip || '').trim() &&
        !!item.practiceStartDate &&
        !serviceAddressError(item)
    );
};

export const serviceAddressComplete = (states: string[], items: ServiceAddressItem[]): boolean =>
    states.every((st) => serviceAddressItemReady(items.find((a) => a.state === st)));

// TIN/EIN 格式（原型：XX-XXXXXXX，连字符可省）
export const TIN_RE = /^\d{2}-?\d{7}$/;

export const additionalW9ItemValid = (item: AdditionalW9Item | undefined): boolean =>
    !!item && TIN_RE.test((item.taxId || '').trim()) && !!(item.nameOnW9 || '').trim() && !!item.fileUrl;

// 每条 W-9（含 primary）都要 TIN 合法 + 表上名字 + 文件
export const additionalW9Complete = (dto: AdditionalW9Dto | undefined): boolean => {
    const items = dto?.items || [];
    return items.length > 0 && items.every(additionalW9ItemValid);
};

export const aetnaComplete = (focusAreas: string[]): boolean =>
    focusAreas.length > 0 && focusAreas.length <= AETNA_FOCUS_MAX;
